import { Content } from '.generated/Feature.CW.model';
import { useAppointmentModal } from 'helpers/Context/AppointmentModalProvider';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import useIsEditing from 'lib/hooks/useIsEditing';
import { useRouter } from 'next/router';
import React, { useRef, useEffect } from 'react';
import { tv } from 'tailwind-variants';

type ContentDrivenAppointmentModalProps = Content.ContentDrivenAppointmentModal;

const ContentDrivenAppointmentModal = (props: ContentDrivenAppointmentModalProps) => {
  const { modalWrapper } = TAILWIND_VARIANTS();
  const modelContainerRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();

  const { isOpenModal, close, queryString, itemId, phoneNumber } = useAppointmentModal();
  const isEditing = useIsEditing();

  // Animation state
  const [isVisible, setIsVisible] = React.useState(false);

  // Store the element that opened the modal
  const lastFocusedElement = useRef<HTMLElement | null>(null);

  // Focus trap logic
  useEffect(() => {
    if (!isOpenModal) return;

    // Save the last focused element
    lastFocusedElement.current = document.activeElement as HTMLElement;

    // Find all focusable elements in the modal
    const modal = modelContainerRef.current;
    if (!modal) return;

    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ];
    const focusableEls = modal.querySelectorAll<HTMLElement>(focusableSelectors.join(','));
    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];

    // Focus the first element
    firstEl?.focus();

    function handleTab(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      if (focusableEls.length === 0) return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }

    modal.addEventListener('keydown', handleTab);

    return () => {
      modal.removeEventListener('keydown', handleTab);
      // Restore focus to the last focused element
      lastFocusedElement.current?.focus();
    };
  }, [isOpenModal]);

  // Handle ESC and click outside
  React.useEffect(() => {
    if (!isOpenModal) return;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
        document.body.style.overflow = 'unset';
      }
    };
    const handleModelEvent = (e: MouseEvent) => {
      if (modelContainerRef.current && !modelContainerRef.current.contains(e.target as Node)) {
        handleClose();
        document.body.style.overflow = 'unset';
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleModelEvent, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleModelEvent, true);
      document.body.style.overflow = 'unset';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenModal]);

  // Custom close to allow animation
  const handleClose = () => {
    setIsVisible(false);
    document.body.style.overflow = 'unset';
    setTimeout(() => {
      close();
    }, 300); // match transition duration
  };

  useEffect(() => {
    if (isOpenModal) {
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
    }
  }, [isOpenModal]);

  // Close modal when route changes
  useEffect(() => {
    const handleRouteChange = () => {
      close();
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router]);

  //if (!isEditing) return null;
  if (!isOpenModal && !isEditing) return null;

  const modalContent = (
    <>
      <div className="relative contentDriven p-12 bg-blue-50">
        <h3
          id="appointment-modal-title"
          className="text-24 md:text-26 leading-[29px] md:leading-[32px] font-bold font-centuryGothic w-[calc(100%-34px)]"
        >
          <PlainTextWrapper field={props?.fields?.appointmentModelTitle} />
        </h3>
        <button
          className="absolute top-12 right-12 text-blue-base"
          onClick={handleClose}
          aria-label="Close"
        >
          <SvgIcon icon="close" className="!w-12 !h-12" />
        </button>
      </div>
      <div className="p-12 grid grid-cols-1 md:grid-cols-2 gap-[42px]">
        <div className="bg-white col-span-1">
          <h4 className="text-20 leading-28 md:text-22 md:leading-31 font-bold font-centuryGothic mb-4">
            <PlainTextWrapper field={props?.fields?.existingPatientSectionTitle} />
          </h4>
          <p className="text-16 leading-24">
            <PlainTextWrapper field={props?.fields?.existingPatientsDescription} />
          </p>
          <LinkWrapper
            field={{
              value: {
                ...props?.fields?.scheduleOnlineCTA?.value,
                href: itemId
                  ? `${props?.fields?.scheduleOnlineCTA?.value?.href}?itemId=${itemId}`
                  : props?.fields?.scheduleOnlineCTA?.value?.href,
                text: props?.fields?.scheduleOnlineCTALabel?.value || '',
              },
            }}
            ctaVariant="primary"
            className="w-fit min-w-full sm:min-w-[300px] mt-12"
            onClick={() => setTimeout(handleClose, 3000)}
          />
          <p className="text-16 leading-24 mt-12">
            <PlainTextWrapper field={props?.fields?.existingPatientsBodyText} />
          </p>
          <LinkWrapper
            field={{
              value: {
                href: `tel:${phoneNumber ? phoneNumber : props?.fields?.existingPatientsPhoneNumber?.value}`,
                text: phoneNumber ? phoneNumber : props?.fields?.existingPatientsPhoneNumber?.value,
              },
            }}
            className="inline-block text-18 leading-24 my-6 text-blue-base font-bold"
          />
        </div>
        <div className="bg-white col-span-1 relative before:absolute before:-top-[23px] md:before:top-0 left-0 md:before:-left-[23px] before:w-full md:before:w-1 before:h-1 md:before:h-full before:bg-gray-100">
          <h4 className="text-20 leading-28 md:text-22 md:leading-31 font-bold font-centuryGothic mb-4">
            <PlainTextWrapper field={props?.fields?.newPatientSectionTitle} />
          </h4>
          <p className="text-16 leading-24">
            <PlainTextWrapper field={props?.fields?.newPatientsDescription} />
          </p>
          <LinkWrapper
            field={{
              value: {
                ...props?.fields?.requestAnAppointmentCTA?.value,
                href: queryString
                  ? `${props?.fields?.requestAnAppointmentCTA?.value?.href}${queryString}`
                  : props?.fields?.requestAnAppointmentCTA?.value?.href,
                text: props?.fields?.requestAnAppointmentCTALabel?.value || '',
              },
            }}
            ctaVariant="primary"
            className="w-fit min-w-full sm:min-w-[300px] mt-12"
            onClick={() => setTimeout(handleClose, 3000)}
          />
          <p className="text-16 leading-24 mt-12">
            <PlainTextWrapper field={props?.fields?.newPatientsBodyText} />
          </p>
          <LinkWrapper
            field={{
              value: {
                href: `tel:${phoneNumber ? phoneNumber : props?.fields?.newPatientsPhoneNumber?.value}`,
                text: phoneNumber ? phoneNumber : props?.fields?.newPatientsPhoneNumber?.value,
              },
            }}
            className="inline-block text-18 leading-24 my-6 text-blue-base font-bold"
          />
        </div>
      </div>
    </>
  );
  return (
    <>
      {isEditing ? (
        <div className="bg-white theme-white max-w-[846px] overflow-hidden md:rounded-md shadow-default m-auto my-10 lg:my-24">
          {modalContent}
        </div>
      ) : (
        <div className={modalWrapper()}>
          <div
            className={`bg-white max-w-[846px] m-auto md:rounded-md overflow-hidden shadow-default w-full transform transition-all duration-300 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
            ref={modelContainerRef}
            style={{ pointerEvents: isVisible ? 'auto' : 'none' }}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="appointment-modal-title"
          >
            {modalContent}
          </div>
        </div>
      )}
    </>
  );
};

export default ContentDrivenAppointmentModal;

const TAILWIND_VARIANTS = tv({
  slots: {
    modalWrapper: [
      'w-full theme-white h-full bg-[#00000040] z-[9999] fixed top-0 left-0 flex items-center justify-center overflow-auto p-10',
    ],
  },
});
